// src/pages/Futures/FuturesTradeHistory.js
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Paper, useTheme, TextField, MenuItem, Button, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton, Tooltip, LinearProgress, Chip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { fetchFutureTradeHistory } from '../../store/Slices/FuturesTradingSlice';

const fmt = (n, d = 2) => (typeof n === 'number' && isFinite(n) ? n.toFixed(d) : '-');
const toDateStr = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return '-'; }
};

export default function FuturesTradeHistory() {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { data: user } = useSelector((s) => s.user);
  const { tradeHistory = [], tradeHistoryLoading } = useSelector((s) => s.futures || {});
  const spotTradingPairs = useSelector((s) => s.assets?.spotTradingPairs || []);

  useEffect(() => { if (user?._id) dispatch(fetchFutureTradeHistory()); }, [dispatch, user?._id]);

  // خريطة _id -> symbol
  const pairIdToSymbol = useMemo(() => {
    const map = {};
    for (const p of spotTradingPairs || []) {
      const sym = p?.symbol || (p?.base_asset_id?.symbol ? `${p.base_asset_id.symbol}USDT` : undefined);
      if (p?._id && sym) map[String(p._id)] = String(sym).toUpperCase();
    }
    return map;
  }, [spotTradingPairs]);

  // أضف رمز لكل سجل
  const rows = useMemo(() => {
    const arr = Array.isArray(tradeHistory) ? tradeHistory : [];
    return arr.map((t) => {
      const fromField = t?.trading_pair_symbol && String(t.trading_pair_symbol).toUpperCase();
      const fromMap   = t?.trading_pair_id && pairIdToSymbol[String(t.trading_pair_id)];
      const fromObj   = typeof t?.trading_pair_id === 'object' && t?.trading_pair_id?.symbol && String(t.trading_pair_id.symbol).toUpperCase();
      const symbol = fromField || fromMap || fromObj || '';
      return { ...t, _symbol: symbol };
    });
  }, [tradeHistory, pairIdToSymbol]);

  // خيارات الفلترة
  const uniquePairs = useMemo(() => {
    const set = new Set();
    rows.forEach(r => r._symbol && set.add(r._symbol));
    return ['ALL', ...Array.from(set)];
  }, [rows]);

  const [filters, setFilters] = useState({
    pair: 'ALL',
    position: 'ALL',
    margin: 'ALL',
    type: 'ALL',
    status: 'ALL',
    q: '',
    from: '',
    to: '',
  });

  const applyFilters = useMemo(() => {
    const from = filters.from ? new Date(filters.from).getTime() : null;
    const to   = filters.to ? new Date(filters.to).getTime() + 86399999 : null; // نهاية اليوم
    const q    = filters.q.trim().toLowerCase();

    return rows.filter((r) => {
      if (filters.pair !== 'ALL' && r._symbol !== filters.pair) return false;
      if (filters.position !== 'ALL' && r.position !== filters.position) return false;
      if (filters.margin !== 'ALL' && r.margin_type !== filters.margin) return false;
      if (filters.type !== 'ALL' && r.order_type !== filters.type) return false;
      if (filters.status !== 'ALL' && r.status !== filters.status) return false;

      const ts = r.created_at ? new Date(r.created_at).getTime() : null;
      if (from && (!ts || ts < from)) return false;
      if (to && (!ts || ts > to)) return false;

      if (q) {
        const hay = `${r._symbol} ${r.position} ${r.margin_type} ${r.order_type} ${r.status} ${r._id}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filters]);

  const clearFilters = () => setFilters({ pair: 'ALL', position: 'ALL', margin: 'ALL', type: 'ALL', status: 'ALL', q: '', from: '', to: '' });

  const refresh = () => { if (user?._id) dispatch(fetchFutureTradeHistory()); };

  return (
    <Paper sx={{ p: 1, bgcolor: 'background.paper' }}>
      {tradeHistoryLoading && <LinearProgress sx={{ height: 2, mb: 1 }} />}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>Futures Trade History</Typography>
        <Chip size="small" label={`${applyFilters.length} / ${rows.length}`} />
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refresh} sx={{ color: theme.palette.text.secondary }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* شريط الفلترة */}
      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <TextField
          select size="small" label="Pair" value={filters.pair}
          onChange={(e) => setFilters((f) => ({ ...f, pair: e.target.value }))}
          sx={{ minWidth: 150 }}
        >
          {uniquePairs.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>

        <TextField select size="small" label="Position" value={filters.position}
          onChange={(e) => setFilters((f) => ({ ...f, position: e.target.value }))}
          sx={{ minWidth: 140 }}
        >
          {['ALL', 'Long', 'Short'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
        </TextField>

        <TextField select size="small" label="Margin" value={filters.margin}
          onChange={(e) => setFilters((f) => ({ ...f, margin: e.target.value }))}
          sx={{ minWidth: 140 }}
        >
          {['ALL', 'Cross', 'Isolated'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
        </TextField>

        <TextField select size="small" label="Type" value={filters.type}
          onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
          sx={{ minWidth: 140 }}
        >
          {['ALL', 'Market', 'Limit'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
        </TextField>

        <TextField select size="small" label="Status" value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          sx={{ minWidth: 160 }}
        >
          {['ALL', 'Filled', 'Closed', 'Canceled', 'Liquidated'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
        </TextField>

        <TextField size="small" label="From" type="date" value={filters.from}
          onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
        <TextField size="small" label="To" type="date" value={filters.to}
          onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          size="small" label="Search" placeholder="symbol / id / status ..."
          value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          sx={{ minWidth: 220, flex: 1 }}
        />

        <Button onClick={clearFilters} startIcon={<ClearAllIcon />} variant="outlined">Clear</Button>
      </Stack>

      {/* الجدول */}
      <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pair</TableCell>
              <TableCell>Pos.</TableCell>
              <TableCell>Margin</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Lev.</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Open</TableCell>
              <TableCell align="right">Close</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applyFilters.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No trades</TableCell></TableRow>
            ) : (
              applyFilters.map((t) => (
                <TableRow key={t._id} hover>
                  <TableCell>{t._symbol || '-'}</TableCell>
                  <TableCell sx={{ color: t.position === 'Long' ? '#0ECB81' : '#F6465D' }}>{t.position}</TableCell>
                  <TableCell>{t.margin_type || '-'}</TableCell>
                  <TableCell>{t.order_type || '-'}</TableCell>
                  <TableCell align="right">{fmt(t.leverage, 0)}x</TableCell>
                  <TableCell align="right">{fmt(t.amount, 4)}</TableCell>
                  <TableCell align="right">{fmt(t.open_price, 2)}</TableCell>
                  <TableCell align="right">{fmt(t.close_price, 2)}</TableCell>
                  <TableCell>{t.status}</TableCell>
                  <TableCell>{toDateStr(t.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}
